/** @file Settings › Password: current, new and repeated; local checks first; other browsers are signed out. */
import { toPose2DError } from '@pose2d/format/toPose2DError';
import type { Translate } from '@pose2d/i18n/types';
import { useState, type SubmitEvent } from 'react';
import type { SettingsApi } from '../../api/createSettingsApi';
import { errorText } from '../../shell/i18n/errorText';

const MIN = 12;
const EMPTY = { current: '', next: '', repeat: '' };
type Fields = typeof EMPTY;
type Status = { kind: 'idle' | 'busy' | 'done' } | { kind: 'error'; text: string };

/** The repeat and the length are checked here; the Worker checks the rest (R-E7-03). */
function localProblem(t: Translate, fields: Fields): string | null {
  if (fields.next !== fields.repeat) return t('password.mismatch');
  if (fields.next.length < MIN) return t('password.tooShort', { min: MIN });
  return null;
}

/** A 401 at `current` is the wrong current password, not a lost session. */
const failureText = (t: Translate, thrown: unknown): string => (toPose2DError(thrown)?.path === 'current' ? t('password.wrongCurrent') : errorText(t, thrown));

/** Props: the translator and the settings client. */
export function PasswordSection({ t, client }: { t: Translate; client: Pick<SettingsApi, 'changePassword'> }) {
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const input = (key: keyof Fields, label: string, auto: string) => (
    <label>{label}<input type="password" autoComplete={auto} value={fields[key]} onChange={(e) => { setFields({ ...fields, [key]: e.target.value }); }} /></label>
  );
  const onSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (status.kind === 'busy') return;
    const problem = localProblem(t, fields);
    if (problem !== null) {
      setStatus({ kind: 'error', text: problem });
      return;
    }
    setStatus({ kind: 'busy' });
    client.changePassword(fields.current, fields.next).then(
      () => { setFields(EMPTY); setStatus({ kind: 'done' }); },
      (thrown: unknown) => { setStatus({ kind: 'error', text: failureText(t, thrown) }); },
    );
  };
  return (
    <section className="shell-card settings-section" aria-labelledby="settings-password">
      <h2 id="settings-password">{t('password.title')}</h2>
      <form className="shell-form" onSubmit={onSubmit}>
        {input('current', t('password.current'), 'current-password')}
        {input('next', t('password.next'), 'new-password')}
        {input('repeat', t('password.repeat'), 'new-password')}
        <button type="submit" disabled={status.kind === 'busy'}>{t('password.submit')}</button>
      </form>
      {status.kind === 'error' ? <p role="alert" className="shell-error">{status.text}</p> : null}
      {status.kind === 'done' ? <p role="status">{t('password.changed')}</p> : null}
    </section>
  );
}
